<script setup lang="ts">
import { onMounted } from 'vue';

import { CoreEventSource } from '@apps/main/new-desktop/core/models';

import { AppSlideUpTransition } from '@libs/ui';
import { isLocationSuggestion } from '@libs/trip-planner-interfaces';
import type {
  AutoCompleteSuggestion,
  IngredientId,
} from '@libs/trip-planner-interfaces';

import { AutoCompleteType } from '../../modules/autoComplete';
import {
  usePriceCalendarStore,
  useStatsigEventsStore,
  useBrowserStore,
} from '../../core/stores';
import { useEditTripStore } from '../../core/stores/editTrip/editTripStore';
import { useConfigurationPanelStore } from '../../core/stores/configurationPanel/configurationPanelStore';
import { TabKey } from '../../core/stores/configurationPanel/configurationPanelStore.model';
import { useDestinationSelectorStore } from '../../core/stores/destinationSelectorStore';
import { useMapStore } from '../../core/stores/mapStore/mapStore';
import type { EditTripSegment } from '../../modules/editTripModal';

import CarTripConfigurationPanel from './components/CarTripConfigurationPanel.vue';
import CarTripModals from './components/CarTripModals.vue';
import CarTripPageLayout from './components/CarTripPageLayout.vue';
import CarTripMap from './components/CarTripMap.vue';
import CarTripFooterDesktop from './components/CarTripFooterDesktop.vue';
import CarTripFooterMobile from './components/CarTripFooterMobile.vue';
import { useCarTripPageStore } from './stores/carTripPageStore';
import { useCarTripModalsStore } from './stores/carTripModalsStore';

const carTripPageStore = useCarTripPageStore();
const configurationPanelStore = useConfigurationPanelStore();
const mapStore = useMapStore();
const editTripStore = useEditTripStore();
const destinationSelectorStore = useDestinationSelectorStore();
const carTripModalsStore = useCarTripModalsStore();
const statsigEventsStore = useStatsigEventsStore();
const priceCalendarStore = usePriceCalendarStore();
const browserStore = useBrowserStore();

function onShowSuggestedTrip(): void {
  statsigEventsStore.logCarPlannerShowSuggestedTrip();
  if (!carTripPageStore.hasUserOpenedPaxTab) {
    carTripModalsStore.openMissedChangePaxesModal();
  } else {
    carTripPageStore.goToTripPlannerOverview();
  }
}

function handleChangeDestination(params: {
  suggestion: AutoCompleteSuggestion;
  ingredientId: IngredientId;
  autocomplete:
    | AutoCompleteType.Destination
    | AutoCompleteType.StartingPosition
    | AutoCompleteType.EndingPosition;
}): void {
  if (params.autocomplete === AutoCompleteType.Destination) {
    destinationSelectorStore.changeDestinationByIngredientId(
      params.suggestion,
      params.ingredientId
    );
  } else if (isLocationSuggestion(params.suggestion)) {
    carTripPageStore.selectUserPosition(
      params.autocomplete,
      params.suggestion.placeId
    );
  }
}

function handleChangeNumberOfNights(segment: EditTripSegment): void {
  editTripStore.changeNumberOfNights(
    segment.ingredientId,
    segment.numberOfNights
  );
}

onMounted(() => {
  carTripPageStore.initializeView();
  statsigEventsStore.logCarPlannerInitiated();
});
</script>

<template>
  <CarTripModals />
  <CarTripPageLayout>
    <template #leftPanel>
      <CarTripConfigurationPanel
        @change-trip-order="editTripStore.changeTripOrder"
        @add-destination="
          destinationSelectorStore.handleConfirmSelection($event, true)
        "
        @change-destination="handleChangeDestination($event)"
        @change-number-of-nights="handleChangeNumberOfNights($event)"
        @remove-destination="destinationSelectorStore.removeDestination"
        @update-pax="carTripPageStore.updatePax"
        @select-my-position="carTripPageStore.selectUserPosition"
        @month-selected="
          priceCalendarStore.selectItineraryMonth(
            $event,
            CoreEventSource.MonthsSelector
          )
        "
      />
    </template>
    <template #rightPanel>
      <CarTripMap
        :map-properties="mapStore.mapProperties"
        :should-load-map="mapStore.isMapLoading"
        @set-dynamic-map-integration="mapStore.setDynamicMapIntegration"
        @on-map-type-id-changed="mapStore.setMapTypeId"
      />
    </template>
    <template
      v-if="
        configurationPanelStore.editTripCarDetails &&
        configurationPanelStore.editTripCarDetails.segments.length > 2
      "
      #footer
    >
      <AppSlideUpTransition>
        <CarTripFooterDesktop
          v-if="browserStore.isLargerThan899px"
          :paxes="carTripPageStore.paxesToDisplay"
          :date="carTripPageStore.dateToDisplay"
          :from-price="carTripPageStore.fromPrice"
          @show-suggested-trip="onShowSuggestedTrip"
          @open-date="carTripPageStore.handleTabSwitch(TabKey.Calendar)"
          @open-paxes="carTripPageStore.handleTabSwitch(TabKey.Pax)"
        />
        <CarTripFooterMobile
          v-else
          :paxes="carTripPageStore.paxesToDisplay"
          :date="carTripPageStore.dateToDisplay"
          :from-price="carTripPageStore.fromPrice"
          @show-suggested-trip="onShowSuggestedTrip"
          @open-date="carTripPageStore.handleTabSwitch(TabKey.Calendar)"
          @open-paxes="carTripPageStore.handleTabSwitch(TabKey.Pax)"
        />
      </AppSlideUpTransition>
    </template>
  </CarTripPageLayout>
</template>
